import { defineStore } from 'pinia';
import { readonly, ref, nextTick } from 'vue';
import type { AxiosResponse } from 'axios';
import { authApi } from '@/entities/Session/';
import { setCurrentSelectedBranch, useBranchLogo, useBranchTheme } from '@/entities/Branch/@x/Session';
import { useTheme } from '@/shared/lib';
import { useNotificationStore } from '@/entities/Notification/@x/Session';
import { usePermissionStore } from '@/entities/Permission/@x/Session';
import { useUserStore } from '@/entities/User/@x/Session';
import { recreateApiClient } from '@/shared/api';

type SessionChangeHandler = () => Promise<void>;

const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref<boolean>(localStorage.getItem('isAuthenticated') === 'true');
  const mustChangePassword = ref(false);
  const selectedBranchId = ref<number | null>(
    localStorage.getItem('selectedBranchId') !== null
      ? Number(localStorage.getItem('selectedBranchId'))
      : null
  );
  const userStore = useUserStore();
  const sessionChangeHandlers = new Set<SessionChangeHandler>();

  // Runs while the cookie still names the current branch and session: anything the server keeps for
  // this branch (a generated record lock) can only be given back before the switch or the logout.
  function onBeforeSessionChange(handler: SessionChangeHandler): () => void {
    sessionChangeHandlers.add(handler);
    return () => {
      sessionChangeHandlers.delete(handler);
    };
  }

  async function runBeforeSessionChange(): Promise<void> {
    await Promise.allSettled([...sessionChangeHandlers].map((handler) => handler()));
  }

  function setAuthenticationState(authenticated: boolean) {
    isAuthenticated.value = authenticated;
    localStorage.setItem('isAuthenticated', authenticated.toString());
  }

  function clearAuthenticationState() {
    isAuthenticated.value = false;
    mustChangePassword.value = false;
    localStorage.removeItem('isAuthenticated');
  }

  function persistSelectedBranchId(branchId: number | undefined) {
    if (branchId == null) return;
    setCurrentSelectedBranch(branchId.toString());
    selectedBranchId.value = branchId;
  }

  async function completePostLoginSetup() {
    recreateApiClient();
    await userStore.me();

    const { applyBranchTheme } = useBranchTheme();
    const { refreshLogosAfterBranchChange } = useBranchLogo();
    const permissionStore = usePermissionStore();

    applyBranchTheme();
    refreshLogosAfterBranchChange();
    permissionStore.buildAbility();
  }

  async function login(credentials: { username: string; password: string; rememberMe: boolean }) {
    try {
      const response = await authApi.login({
        username: credentials.username,
        password: credentials.password,
        rememberMe: credentials.rememberMe,
      });

      if (response.data.mustChangePassword) {
        mustChangePassword.value = true;
        return response;
      }

      if (isAuthenticated.value) {
        setAuthenticationState(false);
        await nextTick();
      }
      setAuthenticationState(true);
      persistSelectedBranchId(response.data.currentBranchId);

      await completePostLoginSetup();

      return response;
    } catch (error) {
      clearAuthenticationState();
      throw error;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const response = await authApi.changePassword({ currentPassword, newPassword });
    mustChangePassword.value = false;

    if (isAuthenticated.value) {
      setAuthenticationState(false);
      await nextTick();
    }
    setAuthenticationState(true);
    persistSelectedBranchId(response.data.currentBranchId);

    try {
      await completePostLoginSetup();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Post-login setup failed after password change:', error);
      }
    }
  }

  async function refreshMainToken(): Promise<AxiosResponse<void>> {
    try {
      const response = await authApi.refreshToken();

      setAuthenticationState(true);

      return response;
    } catch (error) {
      clearAuthenticationState();
      throw error;
    }
  }

  async function switchBranch(branchId: number): Promise<void> {
    await runBeforeSessionChange();
    await authApi.switchBranch(branchId);
    setCurrentSelectedBranch(branchId.toString());
    selectedBranchId.value = branchId;

    await userStore.me();

    const { applyBranchTheme } = useBranchTheme();
    const { refreshLogosAfterBranchChange } = useBranchLogo();
    const notificationStore = useNotificationStore();

    applyBranchTheme();
    refreshLogosAfterBranchChange();
    notificationStore.fetchAllCounts();
  }

  async function logout(): Promise<void> {
    await runBeforeSessionChange();
    try {
      await authApi.logout();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Logout API call failed:', error);
      }
    } finally {
      clearAuthenticationState();
      userStore.clearUser();

      const { clearTheme } = useTheme();
      const { clearSelectedBranch } = useBranchTheme();
      clearTheme();
      clearSelectedBranch();
    }
  }

  return {
    isAuthenticated,
    mustChangePassword: readonly(mustChangePassword),
    selectedBranchId: readonly(selectedBranchId),
    setAuthenticationState,
    clearAuthenticationState,
    login,
    changePassword,
    refreshMainToken,
    switchBranch,
    logout,
    onBeforeSessionChange,
  };
});

export default useAuthStore;
